import React, { type PropsWithChildren } from 'react';
import { styled } from '@compiled/react';
import CustomThemeButton, {
	type ThemeProps,
	type ThemeTokens,
} from '@atlaskit/button/custom-theme-button';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import type { EntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger';
import { useIntl } from '@atlassian/jira-intl';
import { SparkleIcon } from '@atlassian/jira-product-edition-upgrade';
import {
	AnimatedButtonContainer,
	ButtonText,
	EATopNavAnimatedButtonContainer,
	EATopNavButtonText,
	EATopNavPillTheme,
	IconWrapper,
	PillTheme,
	BUTTON_BORDER_RADIUS,
	PillThemeCss,
} from '../../../../common/ui/styled';
import { EATopNavPillCss } from '../../../../common/ui/styled/styled';
import type { EALocation } from '../../../../common/ui/types';
import messages from './messages';

type ButtonProps = {
	triggerRef: EntryPointButtonTrigger;
	onButtonClick: () => void;
	onHoverButton: () => void;
	location: EALocation;
};

export const UpgradeButton = (props: ButtonProps) => {
	const { triggerRef, onButtonClick, onHoverButton, location } = props;
	const { formatMessage } = useIntl();

	const shouldUseTopNavStyles = location === 'TOP_NAV';
	const Container = shouldUseTopNavStyles
		? EATopNavAnimatedButtonContainer
		: AnimatedButtonContainer;
	const TextContainer = shouldUseTopNavStyles ? EATopNavButtonText : ButtonText;

	return (
		<Container>
			<CustomThemeButton
				iconBefore={
					<IconWrapper>
						<SparkleIcon label="" />
					</IconWrapper>
				}
				onClick={onButtonClick}
				theme={shouldUseTopNavStyles ? EATopNavPillTheme : PillTheme}
				onMouseEnter={onHoverButton}
				ref={triggerRef}
				interactionName="edition-awareness-users-remaining-free-pill"
				testId="navigation-apps-sidebar-edition-awareness.ui.free-pill.users-remaining-free-pill.buttons.upgrade"
			>
				<TextContainer>{formatMessage(messages.upgrade)}</TextContainer>
			</CustomThemeButton>
		</Container>
	);
};

export const LimitedUsersLeftButton = (props: ButtonProps) => {
	const { triggerRef, onButtonClick, onHoverButton, location } = props;
	const { formatMessage } = useIntl();

	const shouldUseTopNavStyles = location === 'TOP_NAV';
	const Container = shouldUseTopNavStyles ? TopNavWarningButtonContainer : WarningButtonContainer;
	const TextContainer = shouldUseTopNavStyles ? TopNavWarningButtonText : ButtonText;
	const WarningIconWrapper = shouldUseTopNavStyles ? TopNavDoritoIconWrapper : DoritoIconWrapper;

	return (
		<Container>
			<CustomThemeButton
				iconBefore={
					<WarningIconWrapper>
						<WarningIcon label="" size={shouldUseTopNavStyles ? 'small' : 'medium'} />
					</WarningIconWrapper>
				}
				onClick={onButtonClick}
				theme={shouldUseTopNavStyles ? TopNavWarningButtonTheme : WarningButtonTheme}
				onMouseEnter={onHoverButton}
				ref={triggerRef}
				interactionName="edition-awareness-users-remaining-free-pill"
				testId="navigation-apps-sidebar-edition-awareness.ui.free-pill.users-remaining-free-pill.buttons.limited-users-left"
			>
				<TextContainer>{formatMessage(messages.limitedUsersLeft)}</TextContainer>
			</CustomThemeButton>
		</Container>
	);
};

export const NoUsersLeftButton = (props: ButtonProps) => {
	const { triggerRef, onButtonClick, onHoverButton, location } = props;
	const { formatMessage } = useIntl();

	const shouldUseTopNavStyles = location === 'TOP_NAV';
	const Container = shouldUseTopNavStyles ? TopNavWarningButtonContainer : WarningButtonContainer;
	const TextContainer = shouldUseTopNavStyles ? TopNavWarningButtonText : ButtonText;
	const WarningIconWrapper = shouldUseTopNavStyles ? TopNavDoritoIconWrapper : DoritoIconWrapper;

	return (
		<Container>
			<CustomThemeButton
				iconBefore={
					<WarningIconWrapper>
						<WarningIcon label="" size={shouldUseTopNavStyles ? 'small' : 'medium'} />
					</WarningIconWrapper>
				}
				onClick={onButtonClick}
				theme={shouldUseTopNavStyles ? TopNavWarningButtonTheme : WarningButtonTheme}
				onMouseEnter={onHoverButton}
				ref={triggerRef}
				interactionName="edition-awareness-users-remaining-free-pill"
				testId="navigation-apps-sidebar-edition-awareness.ui.free-pill.users-remaining-free-pill.buttons.no-users-left"
			>
				<TextContainer>{formatMessage(messages.noUsersLeft)}</TextContainer>
			</CustomThemeButton>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DoritoIconWrapper = styled.div({
	marginLeft: token('space.negative.075', '-6px'),
});

const TopNavDoritoIconWrapper = (props: PropsWithChildren) => <>{props.children}</>;

// This adds a gradient border around the element
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WarningButtonContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${BUTTON_BORDER_RADIUS}px`,
	// 1px padding needed to show a gradient border
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	padding: '1px',
	background: `linear-gradient(279.27deg,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        ${token('color.icon.warning', colors.Y500)} 7.53%,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        ${token('color.background.accent.orange.subtler.pressed', colors.Y300)} 51.24%,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        ${token('color.background.accent.orange.subtler.hovered', colors.Y100)} 93.5%)`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TopNavWarningButtonContainer = styled.div({
	borderRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.background.accent.orange.subtler.pressed', colors.Y300)}`,
	height: '30px',
	display: 'inline-flex',
});

const WarningButtonStyles = {
	color: token('color.icon.warning', colors.Y500),

	'&:hover': {
		cursor: 'pointer',
		background: `linear-gradient(79.51deg, ${token(
			'color.background.accent.orange.subtler.pressed',
			colors.Y300,
		)} 0%, ${token('color.icon.warning', colors.Y500)} 98.15%)`,
		color: token('color.text.inverse', '#FFFFFF'),

		svg: {
			// Fix the exclamation mark not being visible when the SVG icon's primary color is white
			fill: token('color.chart.orange.bold', colors.Y400),
		},
	},

	'&:active': {
		background: `linear-gradient(79.51deg,
                ${token('color.chart.orange.bold', colors.Y400)} 0%,
                ${token('color.chart.orange.bolder', colors.Y500)} 100%)`,
	},
};

export const WarningButtonTheme = (
	currentTheme: (props: ThemeProps) => ThemeTokens,
	themeProps: ThemeProps,
) => {
	const theme = currentTheme(themeProps);

	return {
		...theme,
		buttonStyles: {
			...PillThemeCss,
			...WarningButtonStyles,
		},
	};
};

export const TopNavWarningButtonTheme = (
	currentTheme: (props: ThemeProps) => ThemeTokens,
	themeProps: ThemeProps,
) => {
	const theme = currentTheme(themeProps);

	return {
		...theme,
		buttonStyles: {
			...EATopNavPillCss,
			...WarningButtonStyles,
			transition: 'all 0.3s ease-in-out 0s, color 0s',
		},
	};
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TopNavWarningButtonText = styled.span({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '14px',
	lineHeight: '20px',
});
